import { plural } from "@lingui/core/macro";
import { Trans, useLingui } from "@lingui/react/macro";
import { observer } from "mobx-react-lite";
import { useEffect, useState } from "react";

import type { DataTableInstance } from "../../../components/ui-kit/tables/data-table/table-features";

import { DataVisitStatus, PerVisitStudyStats } from "../../../api";
import { DataTable } from "../../../components/ui-kit/tables/data-table";
import { DataModel } from "../../../models/data-model";
import { uniqueValueOptions } from "../../../utils/filter-presets";
import { dateTimeOrNull } from "../../../utils/utils";
import { prettyVisitType } from "../../clinical-studies/utils";

type OverviewRow = PerVisitStudyStats & { key: number };

export const PatientOverviewTable = observer(
  function PatientOverviewTable(props: { data: PerVisitStudyStats[] }) {
    const { t } = useLingui();
    const [model] = useState(() => new DataModel<OverviewRow>([]));

    useEffect(() => {
      // Rows have no id field — synthesize an index-based key.
      model.updateData(
        props.data.map((item, index) => ({ ...item, key: index })),
      );
    }, [props.data, model]);

    return (
      <DataTable
        model={model}
        itemKeyField="key"
        filterNoun={(count) =>
          t`${plural(count, { one: "item", other: "items" })}`
        }
        columns={[
          {
            header: <Trans>Visit Type</Trans>,
            cell: ({ item }) => prettyVisitType(item.visit_type),
            attr: "visit_type",
            filter: {
              kind: "multi",
              group: t`Visit`,
              options: visitTypeOptions,
            },
          },
          {
            header: <Trans>Visit Date</Trans>,
            attr: "visit_datetime",
            cell: ({ item }) => dateTimeOrNull(item.visit_datetime, false),
            sort: true,
            width: 80,
          },
          {
            header: <Trans>Data Name</Trans>,
            attr: "data_name",
            width: 200,
          },
          {
            header: <Trans>Data Status</Trans>,
            attr: "data_status",
            align: "left",
            type: "enum",
            values: DataVisitStatus,
            filter: {
              kind: "multi",
              group: t`Visit`,
              options: uniqueValueOptions<OverviewRow>("data_status"),
            },
            width: 100,
            cell: ({ item }) =>
              item.data_status && item.status_color_hex ? (
                <div className="flex items-center gap-2">
                  <div
                    className="w-3 h-3 rounded-full"
                    style={{ backgroundColor: item.status_color_hex }}
                  />
                  <span>{item.data_status}</span>
                </div>
              ) : (
                <span>-</span>
              ),
          },
        ]}
        rowsPerPage={10}
        readOnly={true}
      />
    );
  },
);

function visitTypeOptions(
  table: DataTableInstance<OverviewRow>,
): Map<string, string> {
  const map = new Map<string, string>();
  for (const row of table.getCoreRowModel().rows) {
    const visitType = row.original.visit_type;
    if (visitType) map.set(visitType, prettyVisitType(visitType));
  }
  return map;
}
